import React, { useId } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  // Alias used in demo
  icon?: React.ReactNode;
}

// CSS to hide number input spinners
const numberInputStyles = `
  [appearance:textfield]
  [&::-webkit-outer-spin-button]:appearance-none
  [&::-webkit-inner-spin-button]:appearance-none
  [&::-webkit-outer-spin-button]:m-0
  [&::-webkit-inner-spin-button]:m-0
`;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', label, error, hint, leftIcon, rightIcon, icon, id, onFocus, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = Boolean(error);
    const effectiveLeftIcon = leftIcon ?? icon;
    const isNumberInput = type === 'number';
    
    // Select all text on focus for number inputs
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (type === 'number') {
        e.target.select();
      }
      // Call any custom onFocus handler passed via props
      onFocus?.(e);
    };
    
    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          {effectiveLeftIcon && (
            <div className="absolute left-0 top-0 flex items-center justify-center h-full w-10 pointer-events-none text-muted-foreground">
              {effectiveLeftIcon}
            </div>
          )}
          
          <input
            ref={ref}
            id={inputId}
            type={type}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background py-2 text-base text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              // Conditional padding: px-2 for number inputs, px-3 for others
              isNumberInput ? 'px-2' : 'px-3',
              hasError && 'border-error-500 focus-visible:ring-error-500',
              effectiveLeftIcon && 'pl-10',
              rightIcon && 'pr-10',
              isNumberInput && numberInputStyles,
              className
            )}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            onFocus={handleFocus}
            {...props}
          />
          
          {rightIcon && (
            <div className="absolute right-0 top-0 flex items-center justify-center h-full w-10 pointer-events-none text-muted-foreground">
              {rightIcon}
            </div>
          )}
        </div>
        
        {hint && !error && (
          <p id={`${inputId}-hint`} className="mt-1 text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        
        {error && (
          <p id={`${inputId}-error`} className="mt-1 text-sm text-error-600">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
